// 🏡📓 Their Place (PORTED_HOME_HANDOFF_2026_09_25): the pure, zero-query
// helpers behind a ported room's room band (the light room) and the
// resident's Notebook journal. No React, no DOM: every function here is
// tested in utils/portedPlace.test.js.

// ── the hidden switch (the SHOW_LIVE_TAGS_HINT pattern: an exported constant,
// test-pinned, so flipping it is a deliberate two-file edit) ────────────────
// OFF: ported rooms keep today's chrome. Nina previews it on ONE device:
// a browser that holds the /admin key (the CustomSpace canCreatePrivate
// precedent) opens any ported room with ?place=1 (remembered on that device;
// ?place=0 forgets it). Nobody else can see it until the constant flips.
export const SHOW_PORTED_PLACE = false;
export const PLACE_PREVIEW_KEY = 'ported_place_preview';
export function placePreviewOn(win = (typeof window !== 'undefined' ? window : null)) {
  try {
    const ls = win && win.localStorage;
    if (!ls || !ls.getItem('adminApiKey')) return false;
    const q = new URLSearchParams(win.location.search).get('place');
    if (q === '1') ls.setItem(PLACE_PREVIEW_KEY, '1');
    if (q === '0') ls.removeItem(PLACE_PREVIEW_KEY);
    return ls.getItem(PLACE_PREVIEW_KEY) === '1';
  } catch (e) { return false; }
}
export const placeOn = (win) => SHOW_PORTED_PLACE || placePreviewOn(win);

// His hand (Caveat) loads only where it is used: index.html carries no
// webfont on purpose (first paint), so the room adds the one link it needs.
// W2c: the same link carries the OWNER's hand (Kalam: the owner's notes, in blue ink).
export function ensureHandFont(doc = (typeof document !== 'undefined' ? document : null)) {
  try {
    if (!doc || doc.querySelector('link[data-ported-hand]')) return;
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@500;600&family=Kalam:wght@400&display=swap';
    link.setAttribute('data-ported-hand', '');
    doc.head.appendChild(link);
  } catch (e) { /* the fallback cursive still reads */ }
}

// ── the owner's clock (handoff §4 "Clock + light") ─────────────────────────
// Night weight n = 1 from 20:30 to 05:00, ramping in over dusk (17:00→20:30)
// and out over dawn (05:00→07:00). Dusk warmth peaks 18:42, dawn at 06:00.
// The four band weights sum to 1 (the light room blends its palettes by them).
export function clockWeights(h) {
  const hour = ((Number(h) % 24) + 24) % 24;
  const n = (hour >= 20.5 || hour < 5) ? 1
    : hour >= 17 ? Math.min(1, (hour - 17) / 3.5)
      : hour < 7 ? Math.max(0, 1 - (hour - 5) / 2) : 0;
  const dusk = Math.max(0, 1 - Math.abs(hour - 18.7) / 2.5);
  const dawn = Math.max(0, 1 - Math.abs(hour - 6) / 1.7);
  const wN = n;
  const wDu = dusk * (1 - 0.5 * n);
  const wDa = dawn * (1 - 0.5 * n);
  const wD = Math.max(0, 1 - n - Math.max(dusk, dawn));
  const sum = (wN + wDu + wDa + wD) || 1;
  return { n, wN: wN / sum, wDu: wDu / sum, wDa: wDa / sum, wD: wD / sum };
}

// Bands: night [21,5) · dawn [5,7) · day [7,17) · dusk [17,21).
export function dayBand(h) {
  const hour = ((Number(h) % 24) + 24) % 24;
  if (hour >= 21 || hour < 5) return 'night';
  if (hour < 7) return 'dawn';
  if (hour < 17) return 'day';
  return 'dusk';
}

// "7:42 pm" (the caption row; ruling 15: never "your clock" beside it).
export function fmtClock(date) {
  const h = date.getHours();
  const m = date.getMinutes();
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
}

// "12:06" · the journal's margin time in his hand (the page is one day, and
// "12:06 pm" wrapped in the 40px margin; the full time rides the title).
export function fmtClockShort(date) {
  return `${((date.getHours() + 11) % 12) + 1}:${String(date.getMinutes()).padStart(2, '0')}`;
}

// Local calendar day key, the viewer's clock ("2026-09-25").
export function localDayKey(date) {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

// 🕐 D4 (W2b): the room runs on the OWNER's clock, the zone stamped at port
// (status `owner_tz`). No zone (an unstamped port) = the viewer's own clock,
// which for the owner is the same thing. → { minutes, dayKey, label }
export function ownerClock(date, tz = null) {
  if (tz) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(date);
      const get = (t) => Number((parts.find((p) => p.type === t) || {}).value);
      const h = get('hour') % 24; const m = get('minute');
      if (Number.isFinite(h) && Number.isFinite(m)) {
        return {
          minutes: h * 60 + m,
          dayKey: `${get('year')}-${String(get('month')).padStart(2, '0')}-${String(get('day')).padStart(2, '0')}`,
          label: `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`,
        };
      }
    } catch (e) { /* an unknown zone: the viewer's clock */ }
  }
  return { minutes: date.getHours() * 60 + date.getMinutes(), dayKey: localDayKey(date), label: fmtClock(date) };
}

// ── the real sky (mirrors justsomeguy-backend/src/ChatGame/skyAlmanac.js) ──
// Same constants, same evening anchor (02:00Z the next day = 7pm Phoenix),
// same "full" test, so the room and the house never disagree about the moon.
export const SYNODIC = 29.530588853;
export const REF_NEW_MOON = Date.UTC(2000, 0, 6, 18, 14);
const FULL_MOON_NAMES = {
  1: 'the Wolf Moon', 2: 'the Snow Moon', 3: 'the Worm Moon', 4: 'the Pink Moon',
  5: 'the Flower Moon', 6: 'the Strawberry Moon', 7: 'the Buck Moon', 8: 'the Sturgeon Moon',
  9: 'the Harvest Moon', 10: "the Hunter's Moon", 11: 'the Beaver Moon', 12: 'the Cold Moon',
};
export const SHOWERS = [
  { m: 1, d: 3, label: 'the Quadrantids' }, { m: 4, d: 22, label: 'the Lyrids' },
  { m: 5, d: 5, label: 'the Eta Aquariids' }, { m: 8, d: 12, label: 'the Perseids' },
  { m: 10, d: 8, label: 'the Draconids' }, { m: 10, d: 21, label: 'the Orionids' },
  { m: 11, d: 17, label: 'the Leonids' }, { m: 12, d: 13, label: 'the Geminids' },
];
const eveningMs = (dayKey) => Date.parse(`${dayKey}T12:00:00Z`) + 14 * 3600e3;
const moonAgeAt = (ms) => ((((ms - REF_NEW_MOON) / 86400e3) % SYNODIC) + SYNODIC) % SYNODIC;

export function moonInfo(dayKey) {
  const age = moonAgeAt(eveningMs(dayKey));
  const illum = (1 - Math.cos((2 * Math.PI * age) / SYNODIC)) / 2;
  const full = Math.abs(age - SYNODIC / 2) < 0.5;
  const fresh = Math.min(age, SYNODIC - age) < 0.5;
  const waxing = age < SYNODIC / 2;
  const month = Number(String(dayKey).slice(5, 7));
  let phase;
  if (fresh) phase = 'new moon';
  else if (full) phase = 'full moon';
  else if (Math.abs(age - SYNODIC / 4) < 0.9) phase = 'first quarter';
  else if (Math.abs(age - (3 * SYNODIC) / 4) < 0.9) phase = 'last quarter';
  else phase = `${waxing ? 'waxing' : 'waning'} ${illum < 0.5 ? 'crescent' : 'gibbous'}`;
  return { age, illum, full, fresh, waxing, phase, name: full ? (FULL_MOON_NAMES[month] || 'the full moon') : null };
}

export function showerOn(dayKey) {
  const m = Number(String(dayKey).slice(5, 7));
  const d = Number(String(dayKey).slice(8, 10));
  const s = SHOWERS.find((x) => x.m === m && x.d === d);
  return s ? s.label : null;
}

// "waxing gibbous · 99%" · "the Harvest Moon" · "+ · the Draconids" on shower nights.
export function skyLabel(dayKey) {
  const moon = moonInfo(dayKey);
  // floor, so a night that is not full never reads "100%"
  const base = moon.full ? moon.name : `${moon.phase} · ${Math.min(99, Math.floor(moon.illum * 100))}%`;
  const shower = showerOn(dayKey);
  return shower ? `${base} · ${shower.replace(/^the /, '')}` : base;
}

// ── names, never guessed pronouns (handoff §2 "Names and pronouns") ────────
// The Notebook's last tab is the owner's first name: first word of
// owner_display_name, max 10 chars with an ellipsis, else "You".
export function ownerTabLabel(ownerDisplayName) {
  const first = String(ownerDisplayName || '').trim().split(/\s+/)[0] || '';
  if (!first) return 'You';
  return first.length > 10 ? `${first.slice(0, 9)}…` : first;
}

// Fill a line about the owner. First mention = the name; later mentions use
// pronouns ONLY when the owner gave them (D11, `owner_pronouns`), else the
// name again. Tokens: {N} name · {subj} · {obj} · {poss} · {be} ("she's",
// "they're", else "Nina's"). Never guessed from a name.
const PRONOUNS = {
  'she/her': { subj: 'she', obj: 'her', poss: 'her', be: "she's" },
  'he/him': { subj: 'he', obj: 'him', poss: 'his', be: "he's" },
  'they/them': { subj: 'they', obj: 'them', poss: 'their', be: "they're" },
};
// the three the server accepts (portedOwner.PRONOUNS; parity-tested)
export const PRONOUN_KEYS = Object.keys(PRONOUNS);
export function fillOwner(template, { name, pronouns } = {}) {
  const who = String(name || '').trim() || 'you';
  const p = PRONOUNS[String(pronouns || '').trim().toLowerCase()] || null;
  const possName = who === 'you' ? 'your' : `${who}'s`;
  const beName = who === 'you' ? "you're" : `${who}'s`;
  return String(template)
    .replace(/\{N\}/g, who)
    .replace(/\{subj\}/g, p ? p.subj : who)
    .replace(/\{obj\}/g, p ? p.obj : who)
    .replace(/\{poss\}/g, p ? p.poss : possName)
    .replace(/\{be\}/g, p ? p.be : beName);
}

// ── time words in his voice ────────────────────────────────────────────────
// Day N together: day 1 is the day the room was made (the viewer's calendar).
export function dayNumber(createdAt, now = new Date()) {
  const c = new Date(createdAt);
  if (Number.isNaN(c.getTime())) return null;
  const a = Date.UTC(c.getFullYear(), c.getMonth(), c.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
}

// "just now" · "this morning" · "this afternoon" · "tonight" · "yesterday" · "day 7"
export function whenWords(ts, { createdAt = null, now = new Date() } = {}) {
  const t = new Date(ts);
  if (Number.isNaN(t.getTime())) return '';
  const mins = (now - t) / 60000;
  if (mins < 10) return 'just now';
  if (localDayKey(t) === localDayKey(now)) {
    const h = t.getHours();
    if (h < 12) return 'this morning';
    if (h < 18) return 'this afternoon';
    return 'tonight';
  }
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (localDayKey(t) === localDayKey(y)) return 'yesterday';
  const n = createdAt ? dayNumber(createdAt, t) : null;
  return n ? `day ${n}` : t.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }).toLowerCase();
}

// "Thu Sep 24" (the page-flip labels) · "Fri Sep 25"
export function pageLabel(dayKey) {
  const [y, m, d] = String(dayKey).split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '');
}

// ── "last spoke 3 min ago" (the caption / rail; ruling 11) ─────────────────
// The RESIDENT's newest message, never the room's newest row (your own
// message would reset it). Normalized action rows name their sender only in
// data.sender_name (processIncomingAction builds {id, type,
// game_instance_player_id, data, created_at}); player_name is a legacy
// fallback some older paths carry.
export function lastSpokeTs(results, residentName) {
  if (!residentName) return 0;
  let maxTs = 0;
  for (const r of (results || [])) {
    for (const a of ((r && r.actions) || [])) {
      if (!a || a.type !== 'public_message') continue;
      if (((a.data && a.data.sender_name) || a.player_name) !== residentName) continue;
      const t = Number(a.data && a.data.timestamp) || Date.parse(a.created_at || '') || 0;
      if (t > maxTs) maxTs = t;
    }
  }
  return maxTs;
}
// The words for a timestamp, read against a clock the caller ticks (W2d: the
// stage re-reads it every 30 s, so "just now" never outlives the moment).
export function agoWords(ts, nowMs = Date.now()) {
  if (!ts) return null;
  const secs = Math.max(0, Math.round((nowMs - ts) / 1000));
  if (secs < 45) return 'just now';
  if (secs < 90) return 'a minute ago';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs >= 36) return `${Math.round(hrs / 24)} days ago`;
  return hrs === 1 ? 'an hour ago' : `${hrs} hours ago`;
}
export function lastSpokeAgo(results, residentName, nowMs = Date.now()) {
  return agoWords(lastSpokeTs(results, residentName), nowMs);
}

// ── the journal's pages, built from the rows the page already holds ────────
// Zero queries. Row shapes (verified 09-25 against the writers):
//   his message   public_message · data { sender_name, message, thoughts, timestamp }
//   his 📌 moment shared_content · data { command:'remember', sender_name,
//                 description:'📌 Worth remembering: …', is_memory_candidate }
//   made things   shared_content · data { url, content_type image|image_url|drawing,
//                 description, sender_name, command?, is_arrival_comic? }
//   his windows   data.command === 'window', text in data.text | data.card_message
const KEEP_TYPES = new Set(['image', 'image_url', 'drawing']);
const firstWord = (s) => String(s || '').trim().split(/\s+/)[0] || '';
const tsOf = (a) => Number(a && a.data && a.data.timestamp) || Date.parse((a && a.created_at) || '') || 0;

// Model text refers to "the user"; in his hand it must name the person
// (§2 "facts must READ in his voice"). A referent swap only, zero LLM.
export function ownerify(text, ownerName) {
  const who = firstWord(ownerName);
  if (!who) return String(text || '');
  return String(text || '')
    .replace(/\b(?:the|my) (?:user|human)(['’]s)\b/gi, `${who}$1`)
    .replace(/\b(?:the|my) (?:user|human)\b/gi, who)
    .replace(/\bUser(['’]s)\b/g, `${who}$1`)
    .replace(/\bUser\b/g, who);
}

// Short lines only in his hand (§2 legibility): cut at a sentence, else a word.
export function handLine(text, max = 220) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const dot = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! '), cut.lastIndexOf('? '));
  return dot > max * 0.45 ? cut.slice(0, dot + 1) : `${cut.slice(0, cut.lastIndexOf(' '))}…`;
}

// Window cards: the same title/body split the feed card uses.
export function windowParts(d) {
  const raw = String((d && (d.text || d.card_message)) || '').trim();
  const nl = raw.indexOf('\n');
  const colon = raw.indexOf(':');
  if (nl > 0 && nl <= 80) return { title: raw.slice(0, nl).trim(), body: raw.slice(nl + 1).trim() || raw };
  if (colon > 0 && colon <= 60) return { title: raw.slice(0, colon).trim(), body: raw.slice(colon + 1).trim() || raw };
  return { title: '', body: raw };
}

// Captions in HIS voice (§2): "Nina's moon doodle, this morning" · "a window i
// hung, day 10" · "the day i got here". Names from the rows, never a pronoun.
export function keepsakeCaption(d, { residentName, ownerName, when }) {
  const mine = !!residentName && d.sender_name === residentName;
  const tail = when ? `, ${when}` : '';
  if (d.command === 'window') return `a window i hung${tail}`;
  if (d.is_arrival_comic) return 'the day i got here';
  // A comic the room posted itself (spaceCommands.postCutsceneContent signs it
  // "The Host", no seat): nobody's picture, and never "The's picture".
  if (d.is_cutscene || (!d.sender_id && d.sender_name === 'The Host')) return `a comic strip${tail}`;
  if (mine) {
    if (d.content_type === 'drawing') return `a doodle i made${tail}`;
    if (d.command === 'imagine') return `something i imagined${tail}`;
    if (d.command === 'build') return `a thing i built${tail}`;
    if (d.command === 'tinker') return `a thing i tinkered with${tail}`;
    return `a picture i made${tail}`;
  }
  const who = firstWord(d.sender_name) || firstWord(ownerName);
  const whose = who ? `${who}'s` : 'your';
  return `${whose} ${d.content_type === 'drawing' ? 'doodle' : 'picture'}${tail}`;
}

// The cork board's captions are the ROOM talking to whoever is looking (W2d;
// the journal's are his): "your doodle, this morning" · "a window Kenley
// hung, day 10" · "Nina's picture". Names from the rows, never a pronoun.
// W2c: a pinned line of his is the OWNER's pin: "you pinned this, just now"
// to the owner, "Nina pinned this, just now" to a guest (a name, never a pronoun).
export function boardCaption(d, { residentName, viewerName, when, ownerName = null, viewerIsOwner = false }) {
  const R = residentName || 'your resident';
  const tail = when ? `, ${when}` : '';
  if (d.pinned_quote) {
    if (viewerIsOwner) return `you pinned this${tail}`;
    const who = firstWord(ownerName);
    return who ? `${who} pinned this${tail}` : `pinned to the board${tail}`;
  }
  if (d.command === 'window') return `a window ${R} hung${tail}`;
  if (d.is_arrival_comic) return `the day ${R} got here`;
  if (d.is_cutscene || (!d.sender_id && d.sender_name === 'The Host')) return `a comic strip${tail}`;
  if (residentName && d.sender_name === residentName) {
    if (d.content_type === 'drawing') return `a doodle ${R} made${tail}`;
    if (d.command === 'imagine') return `something ${R} imagined${tail}`;
    if (d.command === 'build') return `a thing ${R} built${tail}`;
    if (d.command === 'tinker') return `a thing ${R} tinkered with${tail}`;
    return `a picture ${R} made${tail}`;
  }
  const kind = d.content_type === 'drawing' ? 'doodle' : 'picture';
  if (viewerName && d.sender_name === viewerName) return `your ${kind}${tail}`;
  const who = firstWord(d.sender_name);
  return who ? `${who}'s ${kind}${tail}` : `a ${kind}${tail}`;
}

// 💭 Wave 3 (Nina 09-26, item 6): the journal's "the user" → owner-name swap
// follows the CHAT's rule (components/ChatGame/PlaceChatRow ownerSpokeBefore,
// ruling 31): his row names the owner ONLY when the newest human line before
// it is the OWNER's (a guest's line keeps "the user"; none before = no swap;
// his own rows and the host's never count). Handed no ownerSeatId (an older
// caller), the swap is unconditional, as before.
const JOURNAL_CHAT_TYPES = new Set(['public_message', 'human_interjection', 'message', 'speak']);
function humanLinesOf(results, residentName) {
  const out = [];
  for (const r of (results || [])) {
    for (const a of ((r && r.actions) || [])) {
      if (!a || !JOURNAL_CHAT_TYPES.has(a.type)) continue;
      const d = a.data || {};
      if (d.sub_conversation_message === true || d.parent_action_id || d.stage_direction || d.diary_room || d.is_moderator) continue;
      const seat = a.game_instance_player_id || d.sender_id || null;
      if (!seat || (residentName && d.sender_name === residentName)) continue;
      const ts = tsOf(a);
      if (ts) out.push({ seat: String(seat), ts });
    }
  }
  return out.sort((x, y) => x.ts - y.ts);
}
export function buildJournal(results, { residentName, ownerName, createdAt = null, now = new Date(), viewerName = null, ownerSeatId = null } = {}) {
  const thoughts = [];
  const keepsakes = [];
  const moments = [];
  const seenUrls = new Set();
  const humans = ownerSeatId ? humanLinesOf(results, residentName) : null;
  // the owner's name for HIS row at `ts`, by the chat's rule (or always, for an older caller)
  const nameAt = (ts) => {
    if (!humans) return ownerName;
    let last = null;
    for (const h of humans) { if (h.ts <= ts) last = h; else break; }
    return last && last.seat === String(ownerSeatId) ? ownerName : null;
  };
  for (const r of (results || [])) {
    for (const a of ((r && r.actions) || [])) {
      const d = (a && a.data) || {};
      const ts = tsOf(a);
      if (!ts) continue;
      const when = whenWords(ts, { createdAt, now });
      const swapName = nameAt(ts); // the owner's name for HIS words in this row, or null (captions keep ownerName)
      // (⭐ F3's most-reacted-comic stamp, `data.favorite_week`, is RETIRED by
      // ruling 31: the favorite is the one HE chooses on Friday evening, read from
      // the status ported block's `favorites` (favoriteLines below), never a row.)
      if (a.type === 'public_message' && residentName && d.sender_name === residentName && String(d.thoughts || '').trim()) {
        thoughts.push({ id: a.id, ts, text: handLine(ownerify(d.thoughts, swapName)), time: fmtClockShort(new Date(ts)), timeFull: fmtClock(new Date(ts)), dayKey: localDayKey(new Date(ts)) });
      } else if (a.type === 'shared_content' && d.command === 'remember' && (!residentName || d.sender_name === residentName)) {
        const text = String(d.description || '').replace(/^📌\s*Worth remembering:\s*/i, '').trim();
        if (text) moments.push({ id: a.id, ts, text: handLine(ownerify(text, swapName), 160), when: createdAt ? `day ${dayNumber(createdAt, new Date(ts))}` : when });
      } else if (d.command === 'window') {
        const { title, body } = windowParts(d);
        if (body) keepsakes.push({ id: a.id, ts, kind: 'window', title: ownerify(title, swapName), body: handLine(ownerify(body, swapName), 260), caption: keepsakeCaption(d, { residentName, ownerName, when }), board: boardCaption(d, { residentName, viewerName, when }), dayKey: localDayKey(new Date(ts)) });
      } else if (a.type === 'shared_content' && d.url && KEEP_TYPES.has(d.content_type) && !d.is_reaction_gif && !seenUrls.has(d.url)) {
        seenUrls.add(d.url);
        keepsakes.push({ id: a.id, ts, kind: 'photo', url: d.url, caption: keepsakeCaption(d, { residentName, ownerName, when }), board: boardCaption(d, { residentName, viewerName, when }), dayKey: localDayKey(new Date(ts)), ...(d.is_arrival_comic ? { arrival: true } : {}) });
      }
    }
  }
  thoughts.sort((x, y) => x.ts - y.ts);
  keepsakes.sort((x, y) => y.ts - x.ts);
  moments.sort((x, y) => y.ts - x.ts);
  const byDay = new Map();
  const dayOf = (k) => { if (!byDay.has(k)) byDay.set(k, { key: k, entries: [], taped: [] }); return byDay.get(k); };
  thoughts.forEach((t) => dayOf(t.dayKey).entries.push(t));
  [...keepsakes].reverse().forEach((k) => dayOf(k.dayKey).taped.push(k));
  const days = [...byDay.values()].sort((x, y) => (x.key < y.key ? -1 : 1));
  return { onMind: thoughts.length ? thoughts[thoughts.length - 1] : null, days, keepsakes, moments };
}

// ── W2c: the owner's notes in his journal ──────────────────────────────────
// Notes come ONLY from the owner's secret-proven list POST (never a row, never
// the status payload): [{ uid, text, at, state: 'left'|'replied'|'read',
// reply, reply_at }]. They join Keepsakes by time and tape onto their day's
// Today page, as their own kind ('note'): never a photo, never the board
// (portedHomes.boardPins allow-lists what the board shows).
export const NOTE_STATES = ['left', 'replied', 'read'];
export function noteItems(notes) {
  const out = [];
  for (const n of (Array.isArray(notes) ? notes : [])) {
    if (!n || !n.uid) continue;
    const text = String(n.text || '').trim();
    const ts = Date.parse(n.at || '') || 0;
    if (!text || !ts) continue;
    const reply = String(n.reply || '').replace(/\s+/g, ' ').trim();
    const replyTs = reply ? (Date.parse(n.reply_at || '') || 0) : 0;
    const state = reply ? 'replied' : (n.state === 'read' ? 'read' : 'left');
    out.push({
      id: `note:${n.uid}`, kind: 'note', ts, text, state, reply: reply || null, replyTs,
      time: fmtClock(new Date(ts)), dayKey: localDayKey(new Date(ts)),
      replyTime: replyTs ? fmtClockShort(new Date(replyTs)) : null,
      replyTimeFull: replyTs ? fmtClock(new Date(replyTs)) : null,
    });
  }
  return out.sort((x, y) => y.ts - x.ts);
}
// The journal with the notes merged in (Keepsakes newest first; each day's
// taped things oldest first; a day that holds only a note still gets a page).
export function journalWithNotes(journal, notes) {
  const items = noteItems(notes);
  if (!journal || !items.length) return journal;
  const keepsakes = [...(journal.keepsakes || []), ...items].sort((x, y) => (y.ts || 0) - (x.ts || 0));
  const byDay = new Map((journal.days || []).map((d) => [d.key, { ...d, taped: [...(d.taped || [])] }]));
  items.forEach((n) => {
    if (!byDay.has(n.dayKey)) byDay.set(n.dayKey, { key: n.dayKey, entries: [], taped: [] });
    byDay.get(n.dayKey).taped.push(n);
  });
  byDay.forEach((d) => d.taped.sort((x, y) => (x.ts || 0) - (y.ts || 0)));
  const days = [...byDay.values()].sort((x, y) => (x.key < y.key ? -1 : 1));
  return { ...journal, keepsakes, days };
}
// The one note the resident hasn't read yet (one at a time, by the server's rule).
export const noteWaiting = (notes) => (Array.isArray(notes) ? notes : []).some((n) => n && n.state === 'left' && !n.reply);

// ── W2c: his pinned lines on the cork board ────────────────────────────────
// status `pinned_lines` = [{ id: actionId, text, at }] (PUBLIC: his own public
// words, the text taken from the row by the server, never the client). The
// board shows them for everyone; the caption says whose pin it is.
export function pinnedQuotes(pinnedLines, { ownerName = null, viewerIsOwner = false, createdAt = null, now = new Date() } = {}) {
  const out = [];
  for (const p of (Array.isArray(pinnedLines) ? pinnedLines : [])) {
    const text = String((p && p.text) || '').replace(/\s+/g, ' ').trim();
    if (!p || !p.id || !text) continue;
    const ts = Date.parse(p.at || '') || 0;
    const when = ts ? whenWords(ts, { createdAt, now }) : '';
    out.push({
      id: `pin:${p.id}`, actionId: String(p.id), kind: 'quote', ts, text,
      board: boardCaption({ pinned_quote: true }, { ownerName, viewerIsOwner, when }),
    });
  }
  return out;
}
// The 📌 under one of his lines: 'none' · 'pending' (pinned after the last
// time he heard a pin: "{Name} sees it next time") · 'on' (he has heard it).
export function linePinState(actionId, pinnedLines, pinsHeardAt = null) {
  if (!actionId) return 'none';
  const p = (Array.isArray(pinnedLines) ? pinnedLines : []).find((x) => x && String(x.id) === String(actionId));
  if (!p) return 'none';
  const at = Date.parse(p.at || '');
  const heard = Date.parse(pinsHeardAt || '');
  return Number.isFinite(at) && (!Number.isFinite(heard) || at > heard) ? 'pending' : 'on';
}

// ── W3: the arrival comic is his FIRST keepsake ─────────────────────────────
// (§2b.6, ruled: the hatch replaces the comic's splash for new ports; the comic
// becomes the first keepsake.) Keepsakes lists the newest `max`, newest first:
// the arrival comic always closes the page, however many came after it.
export function keepsakePage(keepsakes, max = 12) {
  const list = Array.isArray(keepsakes) ? keepsakes : [];
  const arrival = list.filter((k) => k && k.arrival).sort((a, b) => (a.ts || 0) - (b.ts || 0))[0] || null;
  const rest = list.filter((k) => k && k !== arrival).slice(0, arrival ? max - 1 : max);
  return arrival ? [...rest, arrival] : rest;
}

// ── W5: tasks (§8, D15) ─────────────────────────────────────────────────────
// The owner's list comes ONLY from the owner's secret-proven POST:
// [{ uid, text, kind, when, due_at, state, at, done_at, result }].
// Timing chips (D15: now · when you leave · in 2 hours · tomorrow at 9), all
// on the OWNER's clock (D4). "Due within a day": the 9 o'clock chip is the
// NEXT 9:00, which is always within 24 h (today's, before 9; else tomorrow's).
export function taskChips(now = new Date(), tz = null) {
  const ms = now.getTime();
  const clock = ownerClock(now, tz);
  const toNine = (((9 * 60) - clock.minutes) + 24 * 60) % (24 * 60) || 24 * 60;
  const nine = new Date(Math.round((ms + toNine * 60000) / 60000) * 60000);
  const nineIsToday = clock.minutes < 9 * 60;
  return [
    { key: 'now', when: 'now', label: 'now' },
    { key: 'leave', when: 'leave', label: 'when you leave' },
    { key: 'in2h', when: 'later', label: 'in 2 hours', at: new Date(ms + 2 * 3600e3).toISOString() },
    { key: 'nine', when: 'later', label: nineIsToday ? 'at 9 this morning' : 'tomorrow at 9', at: nine.toISOString() },
  ];
}
// The printed line under a task (the app talking; names only, never a pronoun).
// While he really writes a task whose time has come there is NO printed line:
// the chip carries "on it." in HIS hand instead (taskHandLine; ruling 31, one
// of the two fixed lines allowed in his hand, with the door's "there you are.").
// `next` = this is the one task his next turn takes (the server does one due
// task a turn, the soonest first): only that one can be the one he is on.
export const TASK_HAND_LINE = 'on it.';
export function taskWorking(t, { writing = false, nowMs = Date.now(), next = true } = {}) {
  if (!t || t.state !== 'open' || !writing || !next) return false;
  const dueMs = Date.parse(t.due_at || '');
  return Number.isFinite(dueMs) && dueMs <= nowMs;
}
export function taskHandLine(t, opts = {}) {
  return taskWorking(t, opts) ? TASK_HAND_LINE : null;
}
export function taskStatus(t, { ownerName = null, tz = null, writing = false, nowMs = Date.now(), next = true } = {}) {
  if (!t) return '';
  const name = firstWord(ownerName);
  const dueMs = Date.parse(t.due_at || '');
  const at = Number.isFinite(dueMs) ? ownerClock(new Date(dueMs), tz).label : '';
  if (t.state === 'done') return 'done';
  if (t.state === 'lapsed') return "didn't get to it";
  if (t.state === 'cancelled') return 'taken off the list';
  const dueNow = Number.isFinite(dueMs) && dueMs <= nowMs;
  if (dueNow) return writing && next ? '' : 'up next';
  if (t.when === 'leave') return name ? `after ${name} leaves · on the list` : 'after you leave · on the list';
  if (t.when === 'later' && at) return `at ${at}`;
  return 'on the list';
}
// Open first (soonest first, "when you leave" after the timed ones), then the
// finished ones (newest first, the last five). Cancelled ones leave the page.
export function taskPage(tasks) {
  const list = (Array.isArray(tasks) ? tasks : []).filter((t) => t && t.uid && t.state !== 'cancelled');
  const due = (t) => { const v = Date.parse(t.due_at || ''); return Number.isFinite(v) ? v : Infinity; };
  const open = list.filter((t) => t.state === 'open').sort((a, b) => due(a) - due(b) || (Date.parse(a.at || '') || 0) - (Date.parse(b.at || '') || 0));
  const closed = list.filter((t) => t.state !== 'open').sort((a, b) => (Date.parse(b.done_at || b.at || '') || 0) - (Date.parse(a.done_at || a.at || '') || 0)).slice(0, 5);
  return { open, closed, full: open.length >= 3 };
}
// ── wave 2b: his favorite of the week in Keepsakes (contract 4) ─────────────
// The status ported block's `favorites` (newest first, kept by the server):
// [{ week, kind, ref, text, why, at }], the one HE chose from the week's
// candidates on his Friday-evening ask. One line per favorite, in his hand:
// "my favorite this week: {text} · because {why}" (this week = the ISO week of
// its stamp; last week; else the week it was). Names via ownerify, never a
// pronoun; no favorite = nothing drawn (never an empty claim).
function isoWeekKey(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const y0 = Date.UTC(d.getUTCFullYear(), 0, 1);
  const wk = Math.ceil(((d - y0) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(wk).padStart(2, '0')}`;
}
// (the owner's day, D4: `tz` = status owner_tz; a week key from the server
// ("2026-W40", the owner's ISO week) is trusted, anything else is derived)
const dayNoon = (dayKey) => { const [y, m, d] = String(dayKey).split('-').map(Number); return new Date(y, (m || 1) - 1, d || 1, 12); };
const WEEK_KEY = /^\d{4}-W\d{2}$/;
export function favoriteLines(favorites, { ownerName = null, now = new Date(), tz = null, max = 3 } = {}) {
  const out = [];
  const thisWeek = isoWeekKey(dayNoon(ownerClock(now, tz).dayKey));
  const lastWeek = isoWeekKey(dayNoon(ownerClock(new Date(now.getTime() - 7 * 86400000), tz).dayKey));
  for (const f of (Array.isArray(favorites) ? favorites : [])) {
    if (!f) continue;
    const text = String(f.text || '').replace(/\s+/g, ' ').trim();
    if (!text) continue;
    const ts = Date.parse(f.at || '') || 0;
    const given = String(f.week || '').trim();
    const week = WEEK_KEY.test(given) ? given : (ts ? isoWeekKey(dayNoon(ownerClock(new Date(ts), tz).dayKey)) : '');
    // (`when` = the week phrase alone, for the board's caption: favoritePins below)
    const when = week === thisWeek ? ' this week'
      : week === lastWeek ? ' last week'
        : ts ? `, the week of ${new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toLowerCase()}` : ' that week';
    const label = `my favorite${when}`;
    const why = String(f.why || '').replace(/\s+/g, ' ').trim();
    out.push({
      id: `fav:${week || ts}:${String(f.ref || f.kind || '')}`,
      ts,
      kind: String(f.kind || ''),
      when,
      text: handLine(`${label}: ${ownerify(text, ownerName)}${why ? ` · because ${ownerify(why, ownerName)}` : ''}`, 260),
    });
    if (out.length >= max) break;
  }
  return out;
}
// ⭐📌 Wave 3 (ruling 32): the favorites are PUBLIC keepsakes (the status
// carries them for every viewer), so they hang on the cork board too. The
// pin's words are his line above (his hand); the caption is the ROOM talking
// to whoever looks: "Kenley's favorite this week". Shaped for boardPins'
// allow-list (id fav:…, kind 'fav'); the board holds two (FAV_MAX).
export function favoritePins(favorites, { residentName = null, ownerName = null, now = new Date(), tz = null, max = 2 } = {}) {
  const who = String(residentName || '').trim() || 'Your resident';
  return favoriteLines(favorites, { ownerName, now, tz, max }).map((l) => ({
    id: l.id, kind: 'fav', ts: l.ts, text: l.text, board: `${who}'s favorite${l.when}`,
  }));
}

// "There's something on the desk for you": a task he finished while you were away.
// Bounded by when this visit began: one finished while you watched is no "while you were out".
export function doneWhileAway(tasks, awaySince, returnedAt = Infinity) {
  if (!awaySince) return null;
  return (Array.isArray(tasks) ? tasks : []).find((t) => {
    const at = t && t.state === 'done' ? (Date.parse(t.done_at || '') || 0) : 0;
    return at > awaySince && at < (returnedAt || Infinity);
  }) || null;
}
